import { DatePipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Editor, Toolbar } from 'ngx-editor';
import { SuccessStoryService } from '../../../services/success-story.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SuccessStory } from '@app/store/stories/state';
import { Store } from '@ngrx/store';
import {
  addSuccessStories,
  getSuccessStoryById,
  updateSuccessStories,
} from '@app/store/stories/actions';
import {
  selectHandledStoryChangeSuccessfully,
  selectIsHandlingStoryChange,
  selectIsLoadingSuccessStory,
  selectSuccessStories,
  selectSuccessStoriesData,
  selectSuccessStoryById,
  selectUpdatedId,
} from '@app/store/stories/selector';
import { Observable, withLatestFrom } from 'rxjs';

@Component({
  selector: 'app-add-success-story-page',
  templateUrl: './add-success-story-page.component.html',
  styleUrls: ['./add-success-story-page.component.scss'],
})
export class AddSuccessStoryPageComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  id: number;
  hasChangedContentImages: boolean = false;
  addSuccessStoryFormGroup: FormGroup;
  submitted = false;
  videoData: File;
  coverImageData: File;
  contentImagesData: File[];
  businessChallengesEditor: Editor;
  solutionsEditor: Editor;
  benefitsAndResultsEditor: Editor;
  currentVideoURL: string;
  currentCoverImageURL: string;
  currentContentImagesURL: string[];
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link', 'image'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  successStory: SuccessStory = {
    title: '',
    short_description: '',
    solutions: '',
    cover_image: '',
    content_images: [],
    benefits_results: '',
    business_challenges: '',
    post_date: '',
    video: '',
  };

  isLoading$: Observable<boolean>;

  get isEditing() {
    return Boolean(this.id);
  }

  constructor(
    private route: ActivatedRoute,
    private successStoryService: SuccessStoryService,
    private store: Store<{ successStory: SuccessStory }>,
    private router: Router,
    public datepipe: DatePipe,
    private formBuilder: FormBuilder,
    private _snackBar: MatSnackBar
  ) {
    this.isLoading$ = this.store.select(selectIsLoadingSuccessStory);
    this.currentContentImagesURL = [];
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.params.story;

    this.businessChallengesEditor = new Editor();
    this.solutionsEditor = new Editor();
    this.benefitsAndResultsEditor = new Editor();

    this.addSuccessStoryFormGroup = this.formBuilder.group(
      {
        title: [this.successStory.title, Validators.required],
        short_description: [
          this.successStory.short_description,
          Validators.required,
        ],
        business_challenges: [
          this.successStory.business_challenges,
          Validators.required,
        ],
        solutions: [this.successStory.solutions, Validators.required],
        benefits_results: [
          this.successStory.benefits_results,
          Validators.required,
        ],
        cover_image: [this.successStory.cover_image],
        content_images: [this.successStory.content_images],
        video: [this.successStory.video],
      },
      { updateOn: 'blur' }
    );

    if (!this.isEditing) {
      this.contentImages.setValidators([Validators.required]);
    }

    this.store
      .select(selectHandledStoryChangeSuccessfully)
      .pipe(
        withLatestFrom(
          this.store.select(selectIsHandlingStoryChange),
          this.store.select(selectSuccessStoriesData),
          this.store.select(selectIsLoadingSuccessStory),
          this.store.select(selectUpdatedId)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(
        ([
          handledStoryChange,
          isHandlingStoryChange,
          stories,
          isLoading,
          updatedId,
        ]) => {
          if (!isHandlingStoryChange && handledStoryChange && !isLoading) {
            this._snackBar.open(
              this.isEditing
                ? 'Success story changed successfully.'
                : 'Success story added successfully.',
              'Dismiss',
              {
                duration: 5000,
                verticalPosition: 'top',
                horizontalPosition: 'right',
              }
            );
            this.router.navigate([
              `/success-stories/${updatedId ? updatedId : stories[0]?.id}`,
            ]);
          }
        }
      );

    if (this.id && this.id > 0) {
      this.store.dispatch(getSuccessStoryById({ id: this.id }));
      this.store
        .select(selectSuccessStoryById)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: res => {
            this.title.setValue(res.title);
            this.shortDescription.setValue(res.short_description);
            this.businessChallenges.setValue(res.business_challenges);
            this.solutions.setValue(res.solutions);
            this.benefitsResults.setValue(res.benefits_results);
            this.currentCoverImageURL = res.cover_image;
            res.content_images?.forEach(c =>
              this.currentContentImagesURL.push(c)
            );
            this.currentVideoURL = res.video;

            res.post_date = res.post_date.replace(/"/g, '');
            let currentDateTime = this.datepipe.transform(
              res.post_date,
              'MM-dd-yyyy'
            );
            res.post_date = currentDateTime;
            res.last_modified_date = res.post_date.replace(/"/g, '');
            let currentModifiedDate = this.datepipe.transform(
              res.last_modified_date,
              'MM-dd-yyyy'
            );
            res.last_modified_date = currentModifiedDate;
          },
        });
    }
  }

  get title(): FormControl {
    return this.addSuccessStoryFormGroup.get('title') as FormControl;
  }

  get shortDescription(): AbstractControl {
    return this.addSuccessStoryFormGroup.get('short_description');
  }

  get solutions(): AbstractControl {
    return this.addSuccessStoryFormGroup.get('solutions');
  }

  get coverImage(): AbstractControl {
    return this.addSuccessStoryFormGroup.get('cover_image');
  }

  get contentImages(): AbstractControl {
    return this.addSuccessStoryFormGroup.get('content_images');
  }

  get video(): AbstractControl {
    return this.addSuccessStoryFormGroup.get('video');
  }

  get benefitsResults(): AbstractControl {
    return this.addSuccessStoryFormGroup.get('benefits_results');
  }

  get businessChallenges(): AbstractControl {
    return this.addSuccessStoryFormGroup.get('business_challenges');
  }

  get formControllers(): { [key: string]: AbstractControl } {
    return this.addSuccessStoryFormGroup.controls;
  }

  get coverImageURL(): string {
    return URL.createObjectURL(this.coverImageData);
  }

  getError(controlName: string): ValidationErrors {
    return this.formControllers[controlName].errors;
  }

  onSubmit(formDirective: FormGroupDirective): void {
    if (this.isEditing) {
      this.editSuccessStory(formDirective);
    } else {
      this.addSuccessStory(formDirective);
    }
  }

  addSuccessStory(formDirective: FormGroupDirective): void {
    this.submitted = true;
    this.addSuccessStoryFormGroup.markAllAsTouched();
    this.addSuccessStoryFormGroup.markAsDirty();
    if (this.addSuccessStoryFormGroup.invalid) {
      return;
    }

    this.successStory = { ...this.addSuccessStoryFormGroup.value };

    this.successStory.post_date = this.datepipe.transform(
      new Date(),
      'MM/dd/yyyy h:mm:ss'
    );

    let files = {
      cover_image: this.coverImageData,
      content_images: this.contentImagesData,
      video: this.videoData,
    };

    this.store.dispatch(
      addSuccessStories({ successStory: this.successStory, files })
    );
  }

  editSuccessStory(formDirective: FormGroupDirective): void {
    this.submitted = true;
    this.addSuccessStoryFormGroup.markAllAsTouched();
    this.addSuccessStoryFormGroup.markAsDirty();
    if (this.addSuccessStoryFormGroup.invalid) {
      return;
    }

    this.successStory = {
      ...this.addSuccessStoryFormGroup.value,
      modified_date: this.datepipe.transform(new Date(), 'MM/dd/yyyy h:mm:ss'),
    };

    let files = {
      cover_image: this.coverImageData,
      content_images: this.contentImagesData,
      video: this.videoData,
    };

    this.store.dispatch(
      updateSuccessStories({
        successStory: this.successStory,
        files,
        id: this.id,
      })
    );
  }

  onReset(formDirective: FormGroupDirective): void {
    if (this.isEditing) {
      this.router.navigate(['/', 'success-stories', this.id]);
    } else {
      this.submitted = false;
      this.addSuccessStoryFormGroup.markAsPristine();
      this.addSuccessStoryFormGroup.markAsUntouched();
      this.addSuccessStoryFormGroup.reset();
      formDirective.resetForm();
      this.addSuccessStoryFormGroup.updateValueAndValidity();
    }
  }

  onCoverImageSelected(file: File[]): void {
    this.coverImageData = file[0];
    if (file.length > 0) {
      this.coverImage.setValue(
        this.coverImageData ? this.coverImageData.name : ''
      );
    }
  }

  toBase64 = (file): Promise<string | ArrayBuffer> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  async onContentImageSelected(files: File[]): Promise<void> {
    this.contentImagesData = files;

    if (!this.hasChangedContentImages && this.isEditing) {
      this.hasChangedContentImages = !this.hasChangedContentImages;
      this.currentContentImagesURL = [];
    }

    if (files.length > 0) {
      files.forEach(file => {
        this.currentContentImagesURL.push(URL.createObjectURL(file));
      });
      this.contentImages.setValue(
        this.contentImagesData
          ? this.contentImagesData.reduce(
              (previous: string, file: File, index: number) => {
                return `${previous}${index === 0 ? ' ' : ', '}${file.name}`;
              },
              ''
            )
          : ''
      );
    }
  }

  onVideoSelected(file: File[]): void {
    this.videoData = file[0];
    if (file.length > 0) {
      this.video.setValue(this.videoData ? this.videoData.name : '');
    }
  }

  onPreviewError($event, type: string) {
    if (type === 'video') {
      var reader = new FileReader();
      reader.readAsDataURL(this.videoData);
      reader.onload = event => {
        $event.target.src = (<FileReader>event.target).result;
      };
    } else if (type === 'coverImage') {
      $event.target.src = this.coverImageURL;
    }
  }
}
